import { AuthUtils, LicenseUtils, Product, SharedComputerUtils } from '@portal/portal-core';
import { addSeconds } from 'date-fns';
import { first, flatten, get, isEmpty, keyBy } from 'lodash-es';
import { createSelector } from 'reselect';

import { MASTER_CHECKBOX_ID } from 'constants/cloud-admin';
import { TOOL_BUTTON_NAMES as SUPERADMIN_TOOL_BUTTON_NAMES } from 'constants/super-admin';
import {
  costCenterDropdownInfo,
  costCenterInfoForSelectedAdminProductTab,
  deriveLicenseState,
  emptyArray,
  emptyObj,
  formatDate,
  formatHWADisplay,
  formatListAsObject,
  formatSharedComputerFingerprint,
  formatSharedComputerHWA,
  getObjectMatchesFromSearch,
  getObjectMatchesFromValueMatch,
  getProductAdminAccess,
  isAdmin,
  isSuperAdmin,
  parseDate,
  routeIsAdmin,
  SEARCH_KEY,
  seatActive,
  siteLicenseInfo,
} from 'utils';

import { AuthClientUtils } from 'ducks/auth-client';
import { userCustomFieldFormInfo } from 'ducks/custom-fields/utils';

import type { LicenseViewMode } from 'ducks/super-client/actions';
import type { RootState } from 'reducers';

import { baseSelect, filterUserLastUsedDate, sortCustomFields } from './selector-utils';

export * from './product-version';

const { NAMES_UI_TABS } = Product;

const { getAuthToken } = AuthClientUtils;

// Status/keyword filter shape produced by `filterSelector`.
interface TableFilters {
  filterByStatus?: string;
  filterByValue?: string;
}

// ------------------------------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------------------------------

// Extracts filter/search matches from a list of users or invites
// `records` originates from untyped redux state, so it is a loose boundary array.
const getFilterAndSearchResults = (filters: TableFilters, records: any[]) => {
  const filterByStatus =
    filters.filterByStatus && filters?.filterByStatus !== 'All'
      ? getObjectMatchesFromValueMatch(['status'], null, records, filters.filterByStatus)
      : records;
  // `keyOut` is null at runtime (return matched objects, not a single key); the util's JSDoc types
  // it as `string`. `filterByValue` is optional in the filter shape. Cast both at this boundary to
  // preserve the exact runtime arguments.
  return getObjectMatchesFromSearch(
    [SEARCH_KEY],
    null as unknown as string,
    filterByStatus,
    filters.filterByValue as string,
  );
};

// Restricts invites to pending only
// `invitesArray` items come from untyped redux state (boundary `any`).
export const filterInvitestoPendingOnly = (invitesArray: any[], product: { name: string }) => {
  const now = new Date();
  const pendingInvites: any[] = [];
  const filteredInvites = invitesArray.filter(
    (invite) => invite.productAccessName === product.name && !invite.isCancelled,
  );

  filteredInvites.forEach((invite) => {
    const invitedAt = invite.updatedAt && new Date(invite.updatedAt);
    // Preserve existing runtime: a Date is passed where date-fns expects a number.
    const expirationDate = addSeconds(invitedAt, new Date(invite.ttlSeconds) as unknown as number);
    const expired = now > expirationDate;
    if (!expired) {
      pendingInvites.push(invite);
    }
  });
  return pendingInvites;
};

// `productAccess` items come from untyped redux state (boundary `any`).
const getCurrentProductAccess = (productAccess: any[], selectedProductTab: string) =>
  productAccess.find(({ name }) => name.toLowerCase() === selectedProductTab.toLowerCase());

// ------------------------------------------------------------------------------------------------
// Simple
// ------------------------------------------------------------------------------------------------

export const selectFilterKeyword = baseSelect('header.filterKeyword', '');

export const userAdminSelector = baseSelect('userAdmin', emptyObj);

export const usersObjSelector = baseSelect('userAdmin.users', emptyObj);

export const userLastUsedSelector = baseSelect('userAdmin.lastUsed.user', emptyArray);

export const sharedComputerLastUsedSelector = baseSelect(
  'userAdmin.lastUsed.sharedComputer',
  emptyArray,
);

export const regionSelector = baseSelect('region.selectedRegion');

export const userFilterSelector = baseSelect('cloudAdmin.userFilter', emptyObj);

export const currentTabSelector = baseSelect('productTab.selectedProductTab', NAMES_UI_TABS.flowJo);

// Admin Tools
export const cloudAdminSelector = baseSelect('cloudAdmin', emptyObj);

export const productAccessSelector = baseSelect('userAdmin.costCenter.productAccess', emptyArray);

export const modalsSelector = baseSelect('cloudAdmin.modals', emptyObj);

export const costCenterSelector = baseSelect('userAdmin.costCenter', emptyObj);

export const getAdminInvites = baseSelect('adminClient.adminInvites.invites', emptyArray);

export const getInviteAcceptance = baseSelect(
  'userClient.userInvites.acceptInviteResult',
  emptyObj,
);

export const getResendInvitesResult = baseSelect(
  'adminClient.adminInvites.productAccessInviteUsers.result',
  emptyObj,
);

export const getCancelInvitesResult = baseSelect(
  'adminClient.adminInvites.productAccessCancelInvites.result',
  emptyObj,
);

export const getSelectedRows = baseSelect('cloudAdmin.rows.rowsSelected', emptyArray);

export const alertsSelector = baseSelect('alerts', emptyObj);

export const selectUserId = baseSelect('auth.userId', '');

export const selectUsers = baseSelect('users', emptyObj);

export const selectAuthObj = baseSelect('auth', emptyObj);

export const selectSelf = createSelector(
  selectUsers,
  selectAuthObj,
  (users, auth) => users[auth.userId],
);

export const selectAdminCostCenterState = baseSelect('userAdmin.adminCostCenterList', emptyObj);

// TODO: (Josh) Remove -- dup of selectActiveTool() in super-admin
export const selectSuperAdminActiveTool = baseSelect('superAdmin.toolsBar.selectedTool', emptyObj);

export const selectAdminActiveTool = baseSelect(
  'cloudAdmin.toolsBar.selectedToolButtonName',
  emptyObj,
);

export const getUserAdminAddSharedComputerResult = (state: RootState) =>
  get(state, 'adminClient.sharedComputers.userAdminAddSharedComputer', emptyObj);

export const getUserAdminModifySharedComputerResult = baseSelect(
  'adminClient.sharedComputers.userAdminModifySharedComputer',
  emptyObj,
);

export const getUserAdminAuthorizedDevices = baseSelect('userAdmin.authorizedDevices', emptyObj);

// used to obtain information on user gdpr actions
export const selectGDPRResult = baseSelect('gdpr', emptyObj);

export const selectAllAuthorizedDevices = baseSelect('authorizedDevices.devices', emptyObj);

export const selectAllAuthorizedDeviceRemovalsRemaining = baseSelect(
  'authorizedDevices.removalsRemaining',
  emptyObj,
);

export const selectProducts = baseSelect('products', emptyArray);

export const selectAuthorizedDevicesSagaState = createSelector(
  baseSelect('authorizedDevices', emptyObj),
  (authorizedDevices) =>
    Object.fromEntries(
      // `authorizedDevices` is an untyped redux slice; each saga-state value is a loose object.
      Object.entries(authorizedDevices as Record<string, { loading?: unknown }>).filter(
        ([, val]) => val.loading !== undefined,
      ),
    ),
);

// ------------------------------------------------------------------------------------------------
// Composite
// ------------------------------------------------------------------------------------------------

export const selectIsAuthenticated = (state: RootState): boolean => {
  const reduxAuthFlag = get(state, 'auth.authenticated', true);
  const cookieAuthToken = getAuthToken();
  if (reduxAuthFlag && cookieAuthToken) {
    return true;
  }
  return false;
};

export const selectIsAdminUser = createSelector(userAdminSelector, (userAdmin) =>
  isAdmin(userAdmin),
);

export const selectAdminCostCenters = createSelector(
  selectAdminCostCenterState,
  (adminCostCenters) => Object.values(adminCostCenters),
);

export const selectCostCenters = baseSelect('costCenters', emptyObj);

export const selectCurrentUser = createSelector([selectUsers, selectUserId], (users, userId) =>
  get(users, userId, emptyObj),
);

export const selectCurrentUserPersonalData = createSelector(
  selectCurrentUser,
  ({ firstname, lastname }) => ({ givenName: firstname, familyName: lastname }),
);

export const selectAllProductLicenses = createSelector(
  selectUserId,
  selectCostCenters,
  (userId, costCenters) => {
    if (costCenters) {
      // Maps product name -> cloud-core license objects (untyped boundary).
      const productLicenses: Record<string, any> = {};
      // `NAMES_LICENSED_PRODUCTS` is typed `any` by cloud-core; its values are product-name strings.
      (Object.values(Product.NAMES_LICENSED_PRODUCTS) as string[]).forEach((productName) => {
        productLicenses[productName] = LicenseUtils.getLicensesForProduct(productName, costCenters);
      });
      return productLicenses;
    }
    return undefined;
  },
);

export const selectActiveProductLicense = createSelector(
  selectUserId,
  currentTabSelector,
  selectAllProductLicenses,
  (userId, productName, productLicenseList) => {
    const activeOnly = true;
    const activeProductLicenses: Record<string, any> = {};
    // `productLicenseList` may be undefined; preserve the original (unguarded) access.
    const licenseList = productLicenseList as Record<string, any>;
    Object.keys(licenseList).forEach((product) => {
      activeProductLicenses[product] = LicenseUtils.chooseLicense(
        licenseList[product],
        userId,
        activeOnly,
      );
    });
    return activeProductLicenses[productName] || undefined;
  },
);

export const selectProductLicense = createSelector(
  selectUserId,
  currentTabSelector,
  selectAllProductLicenses,
  (userId, productName, productLicenseList) => {
    const licenses: Record<string, any> = {};
    // `productLicenseList` may be undefined; preserve the original (unguarded) access.
    const licenseList = productLicenseList as Record<string, any>;
    Object.keys(licenseList).forEach((product) => {
      licenses[product] = LicenseUtils.chooseLicense(licenseList[product], userId);
    });
    return licenses[productName] || undefined;
  },
);

export const selectLicenseInfo = createSelector(
  selectUserId,
  selectProductLicense,
  currentTabSelector,
  (userId, productLicense, productName) => {
    if (userId && productLicense && productName) {
      return LicenseUtils.analyzeLicense(productLicense, userId);
    }
    return undefined;
  },
);

export const selectCurrentProductCostCenter = createSelector(selectLicenseInfo, (license) => {
  if (license && license.costCenterId) {
    return license.costCenterId;
  }
  return undefined;
});

export const selectCurrentProductLicenseDetails = createSelector(
  selectProductLicense,
  selectCostCenters,
  (productLicense, costCenters) => {
    if (!productLicense || !costCenters) {
      return undefined;
    }
    const { costCenterId } = productLicense;
    const costCenter = costCenters[costCenterId];
    if (!costCenter) {
      return undefined;
    }
    return {
      costCenterName: costCenter.name,
      costCenterId: costCenter.id,
      productAccessId: productLicense.id,
      productName: productLicense.name,
      productMarketingName: Product.PRODUCT_MARKETING_NAMES[productLicense.name],
    };
  },
);

export const selectProductsLicenseInfo = createSelector(
  selectUserId,
  selectAllProductLicenses,
  (userId, productLicenseInfoList) => {
    if (!productLicenseInfoList || isEmpty(productLicenseInfoList)) {
      return undefined;
    }
    // analyzed license objects (untyped boundary).
    const productInfoArray: any[] = [];
    // `NAMES_LICENSED_PRODUCTS` is typed `any` by cloud-core; its values are product-name strings.
    (Object.values(Product.NAMES_LICENSED_PRODUCTS) as string[]).forEach((productName) => {
      const licensesForProduct = LicenseUtils.chooseLicense(
        productLicenseInfoList[productName],
        userId,
      );
      // this selects the most permissive license for a particular product
      const licenseInfoForProduct = LicenseUtils.analyzeLicense(licensesForProduct, userId);
      if (licenseInfoForProduct) {
        productInfoArray.push(licenseInfoForProduct);
      }
    });
    return productInfoArray;
  },
);

// Returns all licenses grouped by product (not just most permissive)
export const selectAllProductsLicensesGrouped = createSelector(
  selectUserId,
  selectAllProductLicenses,
  (userId, productLicenseInfoList) => {
    if (!productLicenseInfoList || isEmpty(productLicenseInfoList)) {
      return undefined;
    }
    // Maps product name -> sorted analyzed license objects (untyped boundary).
    const groupedLicenses: Record<string, any> = {};
    // `NAMES_LICENSED_PRODUCTS` is typed `any` by cloud-core; its values are product-name strings.
    (Object.values(Product.NAMES_LICENSED_PRODUCTS) as string[]).forEach((productName) => {
      const licensesArray = productLicenseInfoList[productName];
      if (licensesArray && licensesArray.length > 0) {
        // Analyze all licenses for this product
        const analyzedLicenses = licensesArray
          .map((license: any) => LicenseUtils.analyzeLicense(license, userId))
          .filter((license: any) => license !== null);

        // Sort licenses using the same utility
        const sortedLicenses = LicenseUtils.sortLicensesInfo(analyzedLicenses);

        if (sortedLicenses && sortedLicenses.length > 0) {
          groupedLicenses[productName] = sortedLicenses;
        }
      }
    });
    return groupedLicenses;
  },
);

export const selectIsCostCenterInstitutional = createSelector(
  selectLicenseInfo,
  selectCostCenters,
  (productLicenseInfo, costCenters) => {
    const getCostCenterType = (costCenterObj: { type?: string } | undefined) => {
      if (costCenterObj && costCenterObj.type !== 'individual') {
        return true;
      }
      return false;
    };
    if (productLicenseInfo && costCenters && costCenters[productLicenseInfo.costCenterId]) {
      const costCenter = costCenters[productLicenseInfo.costCenterId];
      return getCostCenterType(costCenter);
    }
    return false;
  },
);

export const selectLicenseStatus = createSelector(
  selectUserId,
  selectLicenseInfo,
  currentTabSelector,
  selectCostCenters,
  selectIsCostCenterInstitutional,
  selectIsAdminUser,
  (userId, productLicenseInfo, productName, costCenters, isInstitutional, isAdminUser) => {
    // `deriveLicenseState` derives its param type from JSDoc (only `isInstitutional`); the runtime
    // impl reads all these fields, so pass them through this untyped boundary.
    const licenseState = deriveLicenseState({
      productLicenseInfo,
      productName,
      userId,
      costCenters,
      isInstitutional,
    } as { isInstitutional?: boolean });

    return {
      /**
       * "flowjo" / "seqgeq"
       */
      selectedProductTab: productName,
      productLicenseInfo,
      /**
       * EXPIRED_TRIAL, ACTIVE_LICENSE, etc. -- see `constants/product-tab`
       */
      licenseState,
      isInstitutional,
      isAdmin: isAdminUser,
    };
  },
);

// Combine status filter from tables and keyword filter from header
export const filterSelector = createSelector(
  [selectFilterKeyword, userFilterSelector],
  (keyword, userFilters) => ({
    filterByStatus: userFilters.filterByStatus,
    filterByValue: keyword,
  }),
);

export const filterStatusSelector = createSelector([filterSelector], (filters) =>
  get(filters, 'filterByStatus'),
);
export const filterValueSelector = createSelector([filterSelector], (filters) =>
  get(filters, 'filterByValue'),
);

// Currently viewed product for the Site Administrator
export const currentProductAccessSelector = createSelector(
  [productAccessSelector, currentTabSelector, regionSelector],
  getCurrentProductAccess,
);

// Derived License Status and Period for Admin
export const siteAdminLicenseInfo = createSelector(
  [currentProductAccessSelector],
  (productAccess) => {
    if (productAccess) {
      const { isSiteLicenseActive, startDate, endDate, activityState } =
        siteLicenseInfo(productAccess);
      return {
        isSiteLicenseActive,
        startDate,
        endDate,
        activityState,
      };
    }
    return undefined;
  },
);

// Last successful shared computer result
export const getLastSuccessfullUserAdminSharedComputerResult = createSelector(
  [getUserAdminAddSharedComputerResult, getUserAdminModifySharedComputerResult],
  (addSharedCompResult, modifySharedCompResult) => {
    const addCompUpdatedAt = get(addSharedCompResult, 'result.updatedAt');
    const modifyCompUpdatedAt = get(modifySharedCompResult, 'result.updatedAt');
    // These results come from untyped redux slices; read `success` via lodash `get`.
    if (!get(addSharedCompResult, 'success') || !addCompUpdatedAt) return modifySharedCompResult;
    if (!get(modifySharedCompResult, 'success') || !modifyCompUpdatedAt) return addSharedCompResult;
    if (new Date(addCompUpdatedAt) >= new Date(modifyCompUpdatedAt)) return addSharedCompResult;
    return modifySharedCompResult;
  },
);

// Product access from last successful shared computer result
export const sharedComputerProductAccessSelector = createSelector(
  [getLastSuccessfullUserAdminSharedComputerResult, currentTabSelector, regionSelector],
  (sharedCompResult, selectedProductTab) => {
    const productAccess = get(sharedCompResult, 'result.productAccess', emptyArray);
    return getCurrentProductAccess(productAccess, selectedProductTab);
  },
);

// Current product access or most recennt returned in getLastSuccessfullUserAdminSharedComputerResult()
export const currentProductAccessWithSharedComputerSelector = createSelector(
  [currentProductAccessSelector, sharedComputerProductAccessSelector],
  (currentProductAccess, sharedCompProductAccess) => {
    if (!get(sharedCompProductAccess, 'id')) return currentProductAccess;
    if (!get(currentProductAccess, 'id')) return sharedCompProductAccess;
    return currentProductAccess.id === sharedCompProductAccess.id &&
      new Date(sharedCompProductAccess.updatedAt) >= new Date(currentProductAccess.updatedAt)
      ? sharedCompProductAccess
      : currentProductAccess;
  },
);

export const allCurrentSharedComputersSelector = createSelector(
  [
    currentProductAccessWithSharedComputerSelector,
    usersObjSelector,
    sharedComputerLastUsedSelector,
  ],
  (currentProductAccess, users, lastUsedSharedComputers) => {
    if (currentProductAccess) {
      const { sharedComputers } = currentProductAccess;
      // `sharedComputers` is an untyped map from redux; each entry is a loose shared-computer object.
      return Object.values(sharedComputers || {}).map((sharedComputer: any) => {
        const { id, label, contactLabel } = sharedComputer;
        const hwa = SharedComputerUtils.getSharedComputerHwaIdentifierValue(sharedComputer);
        const fingerprint =
          SharedComputerUtils.getSharedComputerFingerprintIdentifierValue(sharedComputer);
        const matchingLastUsed = lastUsedSharedComputers.filter(
          (lastUsed: any) => lastUsed.device_id === id,
        );
        if (matchingLastUsed.length > 0) {
          const { updated_at, uid } = matchingLastUsed[0]; // eslint-disable-line
          const lastUsedByUser = users[uid];
          if (lastUsedByUser) {
            return {
              ...sharedComputer,
              lastUsedByName: lastUsedByUser.primaryEmail,
              [SEARCH_KEY]: `${label} ${contactLabel} ${formatHWADisplay(
                hwa,
              )} ${formatSharedComputerHWA(hwa)} ${formatSharedComputerFingerprint(fingerprint)}`,
              lastUsedAt: updated_at, // eslint-disable-line
            };
          }
        }
        return {
          ...sharedComputer,
          lastUsedByName: null,
          [SEARCH_KEY]: `${label} ${contactLabel} ${formatHWADisplay(
            hwa,
          )} ${formatSharedComputerHWA(hwa)} ${formatSharedComputerFingerprint(fingerprint)}`,
          lastUsedAt: null,
        };
      });
    }

    return [];
  },
);

export const currentSharedComputersSelector = createSelector(
  [allCurrentSharedComputersSelector, filterSelector],
  (sharedComputers, filters) => getFilterAndSearchResults(filters, sharedComputers),
);

export const currentSharedComputerMaxAllowedSelector = createSelector(
  [currentProductAccessWithSharedComputerSelector, currentTabSelector],
  (currentProductAccess) => {
    if (currentProductAccess) {
      return currentProductAccess.maxSharedComputers;
    }

    return null;
  },
);

export const allUsersSelector = createSelector(
  [usersObjSelector, currentProductAccessSelector, userLastUsedSelector],
  (users, productAccess, lastUsedArray) =>
    (productAccess &&
      // `users` is an untyped map from redux state; each value is a loose user object.
      Object.values(users)
        .filter(({ id }: any) => !!productAccess.users[id])
        .map(({ firstname, lastname, primaryEmail, id }: any) => {
          // null check for a user not associated with the product access

          const { startDate, endDate, suspended, customFieldValues, archivedDate } =
            productAccess.users[id];

          const { createdAt } = users[id];

          // verify that dates are valid, handle them if not
          const activationDate = parseDate(startDate);
          const deactivationDate = parseDate(endDate);
          const createdAtDate = parseDate(createdAt);
          const archivedDateParsed = parseDate(archivedDate);

          const status = seatActive(startDate, endDate, suspended) ? 'active' : 'inactive';

          const lastUsedDate = filterUserLastUsedDate(id, productAccess.id, lastUsedArray);

          return {
            id,
            firstname,
            lastname,
            primaryEmail,
            status,
            activationDate,
            deactivationDate,
            archivedDate: archivedDateParsed,
            createdAtDate,
            lastUsedDate,
            customFieldValues,
            [SEARCH_KEY]: `${firstname} ${lastname} ${primaryEmail} ${status} ${formatDate(
              activationDate,
            )} ${formatDate(deactivationDate)} ${formatDate(lastUsedDate)}`,
          };
        })) ||
        [],
);

// Populate the Admin's users table. This selector takes allUsers and applies filters
export const usersSelector = createSelector([allUsersSelector, filterSelector], (users, filters) =>
  getFilterAndSearchResults(filters, users),
);

export const allInvitesSelector = createSelector(
  [currentProductAccessSelector, getAdminInvites],
  (productAccess, invites) => {
    if (!productAccess) {
      return [];
    }

    return invites
      .filter((invite: any) => invite.productAccessId === productAccess.id && !invite.isCancelled)
      .map((invite: any) => {
        const {
          id = '',
          firstname = '',
          lastname = '',
          emailAddress = '',
          productAccessId = '',
          costCenterId = '',
          phantom = false,
        } = invite;

        const inviteSent = parseDate(invite.createdAt);
        const now = new Date();
        const expirationDate = addSeconds(new Date(invite.updatedAt), invite.ttlSeconds);
        const inviteExpires = parseDate(expirationDate);
        const expired = now > expirationDate;
        const status = now > expirationDate ? 'expired' : 'pending';

        return {
          id,
          firstname,
          lastname,
          emailAddress,
          inviteSent,
          inviteExpires,
          expired,
          status,
          productAccessId,
          costCenterId,
          phantom,
          [SEARCH_KEY]: `${id} ${firstname} ${lastname} ${emailAddress} ${formatDate(
            inviteSent,
          )} ${formatDate(inviteExpires)} ${expired}`,
        };
      });
  },
);

// Populate the invites table.
export const adminInvitesSelector = createSelector(
  [allInvitesSelector, filterSelector],
  (invites, filters) => getFilterAndSearchResults(filters, invites),
);

// Determine selected rows
export const selectedRowsSelector = createSelector([getSelectedRows], (selectedRows) => {
  // For quick lookups, same list is available as an object
  const selectedRowsByID = formatListAsObject(selectedRows);

  // selected rows array should filter 'master' so it isn't sent to server for operations
  // selectedRowsByID needs to include it to indicate it has been checked client-side
  return {
    selectedRows: selectedRows.filter((rowId: string) => rowId !== MASTER_CHECKBOX_ID),
    selectedRowsByID,
  };
});

// These two address FJCLOUD-1341. Restrict checked row items to only those that are visible:
export const selectActiveUserRows = createSelector(
  [getSelectedRows, usersSelector],
  (selectedRows, currentUsers) => {
    const currentUsersByID = keyBy(currentUsers, 'id');
    return selectedRows.filter((id: string) => !!currentUsersByID[id]);
  },
);

export const selectActiveInviteRows = createSelector(
  [getSelectedRows, adminInvitesSelector],
  (selectedRows, currentInvites) => {
    const currentInvitesByID = keyBy(currentInvites, 'id');
    return selectedRows.filter((id: string) => !!currentInvitesByID[id]);
  },
);

// Determine available seats
export const selectAvailableSeats = createSelector(
  [currentTabSelector, productAccessSelector, getAdminInvites],
  (selectedTab, productAccess, invites) => {
    for (const license of productAccess) {
      // ^ "restricted syntax" is the for-of loop, which we want here for the early return
      if (license.name === selectedTab) {
        const pendingInvites = filterInvitestoPendingOnly(invites, license);
        const seats = AuthUtils.getSeatUsageForProductAccess(license, pendingInvites.length);
        return seats;
      }
      return {};
    }
    return {};
  },
);

// In superadmin "admin view", there'll only be a single cost center in state, and it WON'T match
// the user's own ID. Handle that special case;
export const selectSuperAdminCostCenterVisit = createSelector(
  [selectAdminCostCenters, selectUserId],
  (costCenters, userId) => {
    // `costCenters` are untyped redux objects.
    const otherPeoplesCostCenters = costCenters.filter(
      (costCenter: any) => costCenter.owner !== userId,
    );
    if (otherPeoplesCostCenters.length !== 1 || !isSuperAdmin()) {
      return null;
    }

    return first(otherPeoplesCostCenters);
  },
);

export const isAdminCurrentProductSelector = createSelector(
  [currentTabSelector, selectAdminActiveTool, selectAdminCostCenterState, selectUserId],
  (selectedTab, selectedToolName, costCentersState, userId) => {
    if (isSuperAdmin()) {
      // Super admin isn't "owner" of any product access, but should be able to see ALL of them that
      // cost center offers:
      const productAccesses = flatten(
        // `costCentersState` values are untyped redux cost-center objects.
        Object.values(costCentersState).map((costCenter: any) => costCenter.productAccess),
      );
      const productAccessesByName = productAccesses.reduce<Record<string, boolean>>(
        (total, current) => ({
          ...total,
          [current.name]: true,
        }),
        {},
      );
      return productAccessesByName[selectedTab];
    }

    // TODO Refactor to be product agnostic
    // `getProductAdminAccess` derives its param type from JSDoc (only `userId`); pass through the
    // untyped `costCentersState` boundary.
    const adminAccess = getProductAdminAccess({ userId, costCentersState } as { userId?: string });
    return adminAccess[selectedTab];
  },
);

// Raw view-mode map ('search' | 'legacy' per license page, keyed by SuperAdmin tool name) —
// selectors/super-admin derives its per-page selectors from this one; it lives here because that
// module already imports from this one, so importing the other way would be circular.
export const selectLicenseViewModeMap = baseSelect(
  'superAdmin.superClient.licenseViewMode',
  emptyObj,
) as (state: unknown) => Record<string, LicenseViewMode>;

// Check if header keyword filter should be visible
export const displayKeywordFilter = createSelector(
  [
    isAdminCurrentProductSelector,
    selectSuperAdminActiveTool,
    selectAdminActiveTool,
    currentTabSelector,
    selectIsAuthenticated,
    selectLicenseViewModeMap,
  ],
  (isAdminCurrentProduct, superAdminTool, adminTool, selectedTab, isAuthenticated, licenseViewModeMap) => {
    // SuperAdmin visibility by tool. Institutional/Individual License depend on which of that
    // page's two views is showing: the search view has its own dedicated search box (a distinct
    // UX concept from this global one), so this stays hidden there; the legacy full-sync view
    // filters through this box.
    const licenseViewModes = licenseViewModeMap;
    const superAdminTools: Record<string, boolean> = {
      [SUPERADMIN_TOOL_BUTTON_NAMES.INSTITUTION_LICENSE]:
        licenseViewModes[SUPERADMIN_TOOL_BUTTON_NAMES.INSTITUTION_LICENSE] === 'legacy',
      [SUPERADMIN_TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE]:
        licenseViewModes[SUPERADMIN_TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE] === 'legacy',
      [SUPERADMIN_TOOL_BUTTON_NAMES.TASKS]: true,
      [SUPERADMIN_TOOL_BUTTON_NAMES.REPORTS]: false,
      [SUPERADMIN_TOOL_BUTTON_NAMES.USERS]: true,
    };

    // Admin visibility by tool
    const adminTools: Record<string, boolean> = {
      Users: !!isAdminCurrentProduct,
      Invites: !!isAdminCurrentProduct,
      Reports: false,
      Settings: false,
      Shared: true,
    };

    if (routeIsAdmin()) return adminTools[adminTool];
    if (isSuperAdmin() && isAuthenticated) return superAdminTools[superAdminTool];
    return false;
  },
);

export const selectAuthorizedDevicesForCurrentProduct = createSelector(
  selectAllAuthorizedDevices,
  selectLicenseStatus,
  (authorizedDevices, { productLicenseInfo: currentLicenseInfo }) => {
    if (!currentLicenseInfo || !currentLicenseInfo.active) {
      return [];
    }
    const devicesByProductId = Object.keys(authorizedDevices).reduce<any[]>((acc, deviceId) => {
      const device = authorizedDevices[deviceId].authorizedDeviceJson;
      device.extendedOfflineToken = authorizedDevices[deviceId].extendedOfflineToken;
      if (device.productAccessId === currentLicenseInfo.id) {
        return acc.concat([device]);
      }
      return acc;
    }, []);

    // Filter out devices that are in a deactivated state
    const activeDevices = devicesByProductId.filter((d) => d.deactivatedAt === null);
    // Sort the array to return the newest devices first
    // @TODO eventually this should be by 'last login'
    // Dates are cast to number only for the subtraction; runtime behavior is unchanged.
    return activeDevices.sort(
      (a, b) =>
        (new Date(b.lastUsed) as unknown as number) - (new Date(a.lastUsed) as unknown as number),
    );
  },
);

export const selectInfinicytProducts = createSelector(
  selectProducts,
  selectLicenseInfo,
  (products, licenseInfo) => {
    if (!licenseInfo || !licenseInfo.productName) {
      return [];
    }
    // Active/inactive filtering is handled server-side (ProductManager.getProductsForUserAsync
    // returns inactive versions only to verifiers) — filtering again here would strip those
    // versions back out for verifiers too.
    return products.filter((product: any) => product.name === licenseInfo.productName);
  },
);

// don't return a negative number
export const selectAuthorizedDeviceRemainingDeactivationsForCurrentProduct = createSelector(
  selectAllAuthorizedDeviceRemovalsRemaining,
  selectLicenseStatus,
  (authorizedDeviceRemovalsRemaining, { productLicenseInfo: currentLicenseInfo }) => {
    if (!currentLicenseInfo || authorizedDeviceRemovalsRemaining < 0) {
      return 0;
    }
    const remainingDeactivationsObj =
      authorizedDeviceRemovalsRemaining[currentLicenseInfo.id] || {};
    return remainingDeactivationsObj.remainingDeactivations || 0;
  },
);

export const selectMaximumAuthorizedDevicesForCurrentProduct = createSelector(
  selectUserId,
  selectActiveProductLicense,
  (userId, productAccess) => {
    const allLicenseUsers = get(productAccess, 'users', {});
    const currentUserLicense = allLicenseUsers[userId];
    if (!currentUserLicense) {
      return 0;
    }
    return currentUserLicense.maxBoundDevices;
  },
);

export const selectMaxAnnualDeviceDeactivationsForCurrentProduct = createSelector(
  selectUserId,
  selectActiveProductLicense,
  (userId, productAccess) => {
    const allLicenseUsers = get(productAccess, 'users', {});
    const currentUserLicense = allLicenseUsers[userId];
    if (!currentUserLicense) {
      return 4;
    }
    return currentUserLicense.maxAnnualDeviceDeactivations || 4;
  },
);

export const selectAuthorizedDevicesByIdentifierType = createSelector(
  selectAllAuthorizedDevices,
  selectLicenseStatus,
  (authorizedDevices, { productLicenseInfo: currentLicenseInfo }) => {
    if (!currentLicenseInfo || !currentLicenseInfo.active) {
      return { hwas: [], fingerprint: [], total: 0 };
    }

    const devicesByType: { hwas: any[]; fingerprint: any[]; total: number } = {
      hwas: [],
      fingerprint: [],
      total: 0,
    };

    Object.keys(authorizedDevices).forEach((deviceId) => {
      const device = authorizedDevices[deviceId].authorizedDeviceJson;
      if (device.productAccessId === currentLicenseInfo.id && device.deactivatedAt === null) {
        devicesByType.total += 1;
        if (device.identifiers.hwas) {
          devicesByType.hwas.push(device);
        } else if (device.identifiers.fingerprint) {
          devicesByType.fingerprint.push(device);
        }
      }
    });

    return devicesByType;
  },
);

export const getCustomFieldKeysForAdmin = createSelector(
  currentProductAccessSelector,
  (productAccess) => {
    // Custom field keys come from an untyped redux slice; `sortCustomFields` re-derives the values
    // via `Object.values`, so passing this array is runtime-equivalent.
    const customFields = Object.values(productAccess?.customFieldKeys || {});
    if (!customFields.length) {
      return undefined;
    }
    return sortCustomFields(customFields as unknown as Record<string, { orderIndex: number }>);
  },
);

export const currentAdminProductAccessId = createSelector(
  [currentProductAccessSelector],
  (productAccess) => {
    if (productAccess) {
      return productAccess.id;
    }
    return undefined;
  },
);

export const currentAdminProductAccessName = createSelector(
  [currentProductAccessSelector],
  (productAccess) => {
    if (productAccess) {
      return productAccess.name;
    }
    return undefined;
  },
);

export const currentAdminCostCenterId = createSelector([costCenterSelector], (costCenter) => {
  if (costCenter) {
    return costCenter.id;
  }
  return undefined;
});

export const currentAdminCostCenterName = createSelector([costCenterSelector], (costCenter) => {
  if (costCenter) {
    return costCenter.name;
  }
  return undefined;
});

// for the user to accept their product invite, show only one at a time
// for now, assume that if all values are empty the user has not completed
// their custom field. Maybe consider other tricky behind the scenes ... such
// as a string that says "no values supplied by user."
export const selectUserCustomFieldFormInfo = createSelector(
  selectUserId,
  selectCostCenters,
  (userId, costCenters) => {
    if (userId && costCenters) {
      return userCustomFieldFormInfo(userId, costCenters);
    }
    return undefined;
  },
);

export const selectUserCustomFieldValuesResult = baseSelect('customFields.response', undefined);

// create a selector for map of cost center names and their ids
export const selectCostCenterDropdownOptions = createSelector(
  [currentTabSelector, selectAdminCostCenterState],
  (productName, adminCostCenters) => {
    const list = costCenterDropdownInfo(adminCostCenters, productName);
    if (list && list.length) {
      return list.map((item: { name: string }) => {
        return {
          value: item.name,
          label: item.name,
        };
      });
    }
    return [];
  },
);

// create a selector that identifies which license and associated cost center should be the currently selected one
export const selectCostCenterForAdminProductTab = createSelector(
  [currentTabSelector, selectAdminCostCenterState],
  (productName, adminCostCenterList) => {
    return costCenterInfoForSelectedAdminProductTab(adminCostCenterList, productName);
  },
);

// Selectors for the user-client roles slice (populated by GET /api/user/v1/roles after login)
export const selectUserRoles = baseSelect('userClient.userRoles', emptyArray);

export const selectIsVerifier = createSelector(
  selectUserRoles,
  (roles: string[]) => roles.includes('verifier'),
);
